/* eslint-disable react-refresh/only-export-components --
   groupsOf ships with this component: it's the read side of the filter groups
   commit() writes, and CardCarouselEditor needs it to prune groups when the
   source set changes. */
import React from 'react'
import { SourceCode } from '../../../constants/carouselOptions'
import formStyles from '../../../styles/forms.module.scss'
import { Button } from '../../Button'
import type { CarouselMetadata } from './CarouselEditor'
import CarouselFilterBuilder from './CarouselFilterBuilder'
import styles from './CardCarouselEditor.module.scss'

// Filter groups union: within a group keys AND, between groups content
// matching ANY group is included. Every authored config uses `filter_groups`,
// including the one-group common case — the `filters` shorthand is read for
// configs authored before it, never written. The two keys are mutually
// exclusive in the schema, so this component owns the write and drops any
// legacy `filters` it finds.

type Group = Record<string, unknown>

interface CarouselFilterGroupsProps {
  sources: SourceCode[]
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
}

/** The authored groups, reading the legacy `filters` shorthand as a one-group
 * union. Always at least one, so the builder has something to render. */
export const groupsOf = (metadata: CarouselMetadata): Group[] => {
  const stored = metadata.filter_groups
  if (Array.isArray(stored) && stored.length) return stored as Group[]
  return [metadata.filters ?? {}]
}

const CarouselFilterGroups: React.FC<CarouselFilterGroupsProps> = ({ sources, metadata, onChange }) => {
  const groups = groupsOf(metadata)

  /** Write the groups under `filter_groups` and drop any legacy `filters`. A
   * lone empty group is no filtering at all, so it emits neither key. */
  const commit = (next: Group[]) => {
    const updated: CarouselMetadata = { ...metadata }
    delete updated.filters
    const filtering = next.length > 1 || Object.keys(next[0] ?? {}).length > 0
    if (filtering) updated.filter_groups = next
    else delete updated.filter_groups
    onChange(updated)
  }

  const setGroup = (i: number, filters: Group) =>
    commit(groups.map((g, j) => (j === i ? filters : g)))

  const addGroup = () => commit([...groups, {}])

  const removeGroup = (i: number) => commit(groups.filter((_, j) => j !== i))

  return (
    <>
      {groups.map((group, i) => (
        <div key={i}>
          {i > 0 && (
            <div className={styles.groupDivider}>
              <span>OR</span>
              <Button
                variant="secondary"
                size="small"
                onClick={() => removeGroup(i)}
                title="Remove this group"
              >
                Remove group
              </Button>
            </div>
          )}
          <CarouselFilterBuilder
            sources={sources}
            filters={group}
            onChange={(filters) => setGroup(i, filters)}
          />
        </div>
      ))}

      {sources.length > 0 && (
        <div className={styles.addGroup}>
          <Button variant="secondary" size="small" onClick={addGroup}>
            + Add an OR group
          </Button>
          <p className={formStyles.hint}>
            A second group widens the rail: content matching either group is included,
            deduped and sorted together.
          </p>
        </div>
      )}
    </>
  )
}

export default CarouselFilterGroups
